// Core Analytics Types and Enums for FactorEarth Record Application

// Core Analytics Types and Enums for FactorEarth Record Application
import { v4 as uuidv4 } from "uuid";
import { isAnalyticsEnabled } from "../config/analyticsConfig";

// Event types that can be tracked
export enum EventType {
	// Session events
	SESSION_START = "SESSION_START",
	SESSION_HEARTBEAT = "SESSION_HEARTBEAT",
	SESSION_END = "SESSION_END",

	// Form events
	FORM_START = "FORM_START",
	FORM_CREATE = "FORM_CREATE",
	FORM_GROUP_CREATE = "FORM_GROUP_CREATE",
	FORM_GROUP_EDIT = "FORM_GROUP_EDIT",
	FIELD_EDIT = "FIELD_EDIT",

	// Navigation events
	PAGE_VIEW = "PAGE_VIEW",
	REFRESH = "REFRESH",
	ROUTE_CHANGE = "ROUTE_CHANGE",
	MENU_CLICK = "MENU_CLICK",

	// Project events
	PROJECT_CREATE = "PROJECT_CREATE",
	PROJECT_EDIT = "PROJECT_EDIT",

	// Template events
	TEMPLATE_CREATE = "TEMPLATE_CREATE",
	TEMPLATE_EDIT = "TEMPLATE_EDIT",
	TEMPLATE_DELETE = "TEMPLATE_DELETE",

	// Export events
	EXPORT = "EXPORT",
	EXPORT_START = "EXPORT_START",
	EXPORT_SUCCESS = "EXPORT_SUCCESS",
	EXPORT_FAILURE = "EXPORT_FAILURE",
	DATA_EXPORT = "DATA_EXPORT",

	// History events
	HISTORY_ACCESS = "HISTORY_ACCESS",
	HISTORY_REVERT = "HISTORY_REVERT",
}

// Device types for categorization
export enum DeviceType {
	MOBILE = "mobile",
	TABLET = "tablet",
	DESKTOP = "desktop",
}

// Core analytics event interface
export interface AnalyticsEvent {
	id: string;
	eventType: EventType; // Legacy field for backward compatibility
	userId: string; // User document ID (user.id from AwsDefaults)
	groupId: string; // User's group/organization (user.group from AwsDefaults)
	timestamp: string; // ISO 8601
	clientTimestamp?: string; // ISO 8601
	sessionId: string;
	deviceType: DeviceType;
	metadata: Record<string, any>;
	appVersion?: string;
	offlineQueued?: boolean;
	ttl?: number;
	// New standardized fields
	category?: string;
	action?: string;
	label?: string;
	value?: number;
}

// NOTE: CreateEventInput has been moved to analyticsStandards.ts
// Import from there for the new standardized interface

// Session information
export interface SessionInfo {
	sessionId: string;
	startTime: Date;
	lastHeartbeat: Date;
	isActive: boolean;
}

// Configuration for analytics service
export interface AnalyticsConfig {
	enabled: boolean;
	batchSize: number;
	flushInterval: number; // minutes
	heartbeatInterval: number; // minutes
	endpoint?: string; // Lambda endpoint override
	debug: boolean;
}

// Response from Lambda function
export interface AnalyticsResponse {
	success: boolean;
	summary: {
		received: number;
		processed: number;
		written: number;
		failed: number;
		total: number;
	};
	failedEventIds?: string[];
}

// Error types for analytics operations
export class AnalyticsError extends Error {
	constructor(
		message: string,
		public readonly code: string = "ANALYTICS_ERROR",
		public readonly retryable: boolean = true,
	) {
		super(message);
		this.name = "AnalyticsError";
	}
}

export class ValidationError extends AnalyticsError {
	constructor(message: string) {
		super(message, "VALIDATION_ERROR", false);
		this.name = "ValidationError";
	}
}

export class NetworkError extends AnalyticsError {
	constructor(message: string, retryable: boolean = true) {
		super(message, "NETWORK_ERROR", retryable);
		this.name = "NetworkError";
	}
}

// Utility functions
export const generateEventId = (): string => {
	return uuidv4(); // Use consistent UUID v4 format
};

export const calculateTTL = (days: number = 30): number => {
	return Math.floor(Date.now() / 1000) + days * 24 * 60 * 60;
};

export const isValidEventType = (eventType: string): eventType is EventType => {
	return Object.values(EventType).includes(eventType as EventType);
};

export const createTimestamp = (): string => {
	return new Date().toISOString();
};

// Default configuration - SIMPLIFIED!
export const DEFAULT_ANALYTICS_CONFIG: AnalyticsConfig = {
	enabled: isAnalyticsEnabled(), // Centralized check for DEV_MODE + VITE_APP_ANALYTICS_ENABLED
	batchSize: 50,
	flushInterval: 5, // 5 minutes
	heartbeatInterval: 2, // 2 minutes
	debug: import.meta.env.NODE_ENV === "development",
};

/**
 * Types for Analytics Display Module
 *
 * This module handles fetching, processing, and displaying analytics aggregates
 * from the DynamoDB AnalyticsAggregate table.
 */

// Metric types from the AnalyticsProcessor
export enum MetricType {
	USER_HOURLY = "user-hourly",
	USER_DAILY = "user-daily",
	GROUP_HOURLY = "group-hourly",
	GROUP_DAILY = "group-daily",
	SYSTEM_HOURLY = "system-hourly",
	SYSTEM_DAILY = "system-daily",
}

// Base interface matching DynamoDB aggregate structure
export interface AnalyticsAggregate {
	id: string;
	metricType: string;
	period: string;
	value: number;
	groupId?: string;
	metadata: Record<string, any>;
	ttl: number;
	createdAt: string;
	updatedAt: string;
}

// Time range for querying metrics
export interface DateRange {
	startDate: string; // ISO date string
	endDate: string; // ISO date string
}

// User metrics extracted from aggregate metadata
export interface UserMetrics {
	userId: string;
	period: string;
	totalEvents: number;
	uniqueSessions: number;
	formStarts: number;
	formCreates: number;
	formGroupCreates: number;
	formGroupEdits: number;
	fieldEdits: number;
	refreshes: number;
	pageViews: number;
	routeChanges: number;
	historyReverts: number;
	exports: number;
	projectsCreated: number;
	templatesCreated: number;
	templatesEdited: number;
	averageSessionDuration?: number;
	totalSessionDuration?: number;
	mostActiveHour?: number;
	eventTypes: Record<string, number>;
	deviceBreakdown: Record<string, number>;
	appVersions: Record<string, number>;
	topUrls: Array<{ url: string; count: number }>;
	formTemplateIds: Array<string>;
	window: {
		type: string;
		period: string;
		startTime: string;
		endTime: string;
	};
}

// Group metrics extracted from aggregate metadata
export interface GroupMetrics {
	groupId: string;
	period: string;
	totalEvents: number;
	uniqueSessions: number;
	uniqueUsers: number;
	totalProjectsCreated: number;
	totalTemplatesCreated: number;
	totalExports: number;
	averageUserActivity: number;
	topEventTypes: Array<{ eventType: string; count: number }>;
	eventTypes: Record<string, number>;
	deviceDistribution: Record<string, number>;
	window: {
		type: string;
		period: string;
		startTime: string;
		endTime: string;
	};
}

// System metrics extracted from aggregate metadata
export interface SystemMetrics {
	period: string;
	totalEvents: number;
	uniqueSessions: number;
	uniqueUsers: number;
	uniqueGroups: number;
	totalFormStarts: number;
	totalExports: number;
	systemLoad: number;
	errorRate: number;
	averageResponseTime?: number;
	peakHour?: number;
	eventTypes: Record<string, number>;
	formTemplateIds: Array<string>;
	window: {
		type: string;
		period: string;
		startTime: string;
		endTime: string;
	};
}

// Aggregated dashboard data
export interface DashboardData {
	dateRange: DateRange;
	summary: {
		totalUsers: number;
		totalSessions: number;
		totalEvents: number;
		totalExports: number;
		averageSessionDuration: number;
	};
	userMetrics: UserMetrics[];
	groupMetrics: GroupMetrics[];
	systemMetrics: SystemMetrics[];
	timeSeriesData: TimeSeriesData;
}

// Time series data for charts
export interface TimeSeriesData {
	hourlyActivity: Array<{
		hour: string;
		events: number;
		users: number;
		sessions: number;
	}>;
	dailyActivity: Array<{
		date: string;
		events: number;
		users: number;
		sessions: number;
		exports: number;
	}>;
	eventTypeBreakdown: Array<{
		eventType: string;
		count: number;
		percentage: number;
	}>;
	deviceTypeBreakdown: Array<{
		deviceType: string;
		count: number;
		percentage: number;
	}>;
}

// Filter options for analytics queries
export interface AnalyticsFilters {
	metricType?: MetricType;
	groupId?: string;
	dateRange?: DateRange;
	limit?: number;
	nextToken?: string;
}

// Query result structure
export interface AnalyticsQueryResult {
	aggregates: AnalyticsAggregate[];
	nextToken?: string;
	hasMore: boolean;
}

// Hook return types
export interface UseAnalyticsDataResult {
	data: DashboardData | null;
	loading: boolean;
	error: string | null;
	refresh: () => Promise<void>;
	setFilters: (filters: AnalyticsFilters) => void;
	filters: AnalyticsFilters;
}

// Chart configuration
export interface ChartConfig {
	type: "line" | "bar" | "pie" | "area";
	title: string;
	data: any[];
	xAxis?: string;
	yAxis?: string;
	colors?: string[];
}

// Export all analytics types from this file
